import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Subject, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ChatComposerComponent } from 'src/app/components/chat-composer/chat-composer.component';
import { ChatThread, ChatTurn, Conversation } from 'src/app/models/chat';
import {
    ChatMarkdownService, NOT_VERIFIED_ANCHOR, NOT_VERIFIED_CLASS, NOT_VERIFIED_EXPLANATION,
} from 'src/app/services/chat-markdown/chat-markdown.service';
import { ChatStateService } from 'src/app/services/chat-state/chat-state.service';
import { SectionTooltipService } from 'src/app/services/section-tooltip/section-tooltip.service';
import { UserService } from 'src/app/services/user/user.service';
import { AlertDialog, AlertDialogData } from 'src/app/util/alert-dialog/alert-dialog';

// Below this the sidebar covers the page instead of sitting beside it: see the stylesheet
const NARROW_WIDTH = 800;
const SIDEBAR_KEY = 'gps_chat_sidebar';
// How far from the end of a conversation before the way back down is offered
const AWAY_FROM_END = 120;

/**
 * The AI chat, a page within the app's frame: the user's chats down the left, and one of
 * them - or the start of a new one - on the right.
 */
@Component({
    selector: 'app-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {
    /** Questions to start a chat with, once the API has given them */
    suggestions: string[] = [];

    threads: ChatThread[] = [];
    /** The id of the conversation in the address, or null for a chat not yet started */
    threadId: string | null = null;
    conversation: Conversation | null = null;
    loading = false;
    /** The conversation in the address could not be fetched */
    unavailable = false;
    firstName = '';
    sidebarOpen = true;
    /** The answer just copied, to say so beside it */
    copied: ChatTurn | null = null;
    awayFromEnd = false;
    /** Something the user asked for could not be done */
    problem: string | null = null;

    /** A conversation has been put on the page and not yet scrolled to where it is read from */
    private settling = false;
    /** Questions typed and not sent, by the chat each was typed in: '' for one not yet started */
    private drafts = new Map<string, string>();
    private destroy$ = new Subject<void>();
    private hover$ = new Subject<{ url: string, x: number, y: number } | null>();

    @ViewChild('scroller') private scroller: ElementRef<HTMLElement>;
    @ViewChild(ChatComposerComponent) private composer: ChatComposerComponent;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        public chatState: ChatStateService,
        private userService: UserService,
        private markdown: ChatMarkdownService,
        private tooltipService: SectionTooltipService,
        private dialog: MatDialog,
    ) { }

    get title(): string {
        // The list has the latest name, but only the chats loaded so far
        return this.threads.find(thread => thread.thread_id === this.threadId)?.title
            ?? this.conversation?.title ?? '';
    }

    get greeting(): string {
        const hour = new Date().getHours();
        const time = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
        return this.firstName ? `${time}, ${this.firstName}` : time;
    }

    /** The question typed, and not sent, in the chat on screen */
    get draft(): string {
        return this.drafts.get(this.threadId ?? '') ?? '';
    }

    private get narrow(): boolean {
        return window.innerWidth < NARROW_WIDTH;
    }

    ngOnInit(): void {
        this.sidebarOpen = !this.narrow && this.stored(SIDEBAR_KEY) !== 'closed';

        this.chatState.loadThreads();
        this.chatState.threads$.pipe(takeUntil(this.destroy$)).subscribe(threads => (this.threads = threads));
        this.userService.currentUser().pipe(takeUntil(this.destroy$))
            .subscribe(user => (this.firstName = user?.first_name ?? ''));
        this.chatState.suggestedQuestions().pipe(takeUntil(this.destroy$))
            .subscribe(suggestions => (this.suggestions = suggestions));

        this.route.paramMap.pipe(
            map(params => params.get('threadId')),
            tap(threadId => {
                this.threadId = threadId;
                this.conversation = null;
                this.unavailable = false;
                this.loading = !!threadId;
                this.awayFromEnd = false;
                this.tooltipService.hide();
            }),
            switchMap(threadId => !threadId ? of(null) : this.chatState.open(threadId).pipe(
                catchError(() => {
                    this.loading = false;
                    this.unavailable = true;
                    return EMPTY;
                }),
            )),
            takeUntil(this.destroy$),
        ).subscribe(conversation => {
            this.conversation = conversation;
            this.loading = false;
            this.settling = true;
            this.afterRender(() => {
                this.settling = false;
                if (conversation?.answering) {
                    // Arrived with a question just asked
                    this.scrollToLastQuestion();
                } else {
                    this.scrollToEnd();
                }
                this.composer?.focus();
            });
        });

        // Wait on a link before fetching its preview
        this.hover$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(hovered => {
            if (hovered) {
                this.showTooltip(hovered.url, hovered.x, hovered.y);
            } else if (!this.tooltipService.tooltipHovered) {
                this.tooltipService.hide();
            }
        });
    }

    /** The conversation also grows under the reader, as an answer arrives. */
    ngAfterViewChecked(): void {
        const away = this.isAwayFromEnd();
        if (away !== this.awayFromEnd && !this.settling) {
            // Not during the check that found it out
            Promise.resolve().then(() => (this.awayFromEnd = away));
        }
    }

    onScroll(): void {
        this.awayFromEnd = this.isAwayFromEnd();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.tooltipService.hide();
    }

    /** Ask a question: in the conversation on screen, or in a new one, which is then opened. */
    ask(question: string): void {
        if (this.threadId && !this.conversation) {
            // Still fetching the conversation the question would belong to
            return;
        }
        const threadId = this.chatState.ask(question, this.threadId ?? undefined);
        if (!threadId) {
            return;
        }
        if (threadId !== this.threadId) {
            this.router.navigate(['/chat', threadId]);
        } else {
            this.afterRender(() => this.scrollToLastQuestion(true));
        }
    }

    setDraft(text: string): void {
        this.drafts.set(this.threadId ?? '', text);
    }

    async copy(turn: ChatTurn): Promise<void> {
        if (await this.markdown.copy(turn.markdown)) {
            this.copied = turn;
            setTimeout(() => {
                if (this.copied === turn) {
                    this.copied = null;
                }
            }, 2000);
        }
    }

    toggleSidebar(open: boolean): void {
        this.sidebarOpen = open;
        if (!this.narrow) {
            this.store(SIDEBAR_KEY, open ? 'open' : 'closed');
        }
    }

    /** A link in the sidebar was followed. Where the sidebar covers the page, get it out of the way. */
    onNavigated(): void {
        if (this.narrow) {
            this.sidebarOpen = false;
        }
        // The same address, when a new chat is asked for from a new chat
        this.composer?.focus();
    }

    renameThread(change: { thread: ChatThread, title: string }): void {
        this.problem = null;
        this.chatState.renameThread(change.thread.thread_id, change.title).subscribe({
            error: () => (this.problem = 'The chat could not be renamed. Please try again.'),
        });
    }

    /** Delete a chat, once the user has confirmed it. */
    deleteThread(thread: ChatThread): void {
        const data: AlertDialogData = {
            title: 'Delete chat?',
            message: `“${thread.title}” will be removed from your chats.`,
            cancelButtonText: 'Cancel',
            okButtonText: 'Delete',
        };
        this.dialog.open(AlertDialog, { width: '400px', data }).afterClosed().subscribe(confirmed => {
            if (!confirmed) {
                return;
            }
            this.problem = null;
            this.chatState.deleteThread(thread.thread_id).subscribe({
                next: () => {
                    this.drafts.delete(thread.thread_id);
                    if (thread.thread_id === this.threadId) {
                        this.router.navigate(['/chat']);
                    }
                },
                error: () => (this.problem = 'The chat could not be deleted. Please try again.'),
            });
        });
    }

    scrollToEnd(smooth: boolean = false): void {
        const box = this.scroller?.nativeElement;
        box?.scrollTo({ top: box.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    }

    /** Preview the section a citation links to, or explain the "not verified" mark, while it is hovered. */
    onAnswerMouseOver(event: MouseEvent): void {
        const link = this.answerLink(event.target);
        if (!link) {
            return;
        }
        const href = link.classList.contains(NOT_VERIFIED_CLASS) ? NOT_VERIFIED_ANCHOR : link.getAttribute('href');
        if (href && (href.includes('/country/') || href === NOT_VERIFIED_ANCHOR)) {
            const rect = link.getBoundingClientRect();
            this.tooltipService.cancelHide();
            this.hover$.next({ url: href, x: rect.left, y: rect.bottom + 5 });
        }
    }

    onAnswerMouseOut(event: MouseEvent): void {
        const link = this.answerLink(event.target);
        if (link && !link.contains(event.relatedTarget as Node | null)) {
            this.hover$.next(null);
            this.tooltipService.scheduleHide();
        }
    }

    /** A click anywhere but on a citation or its preview puts the preview away. */
    onPageClick(event: MouseEvent): void {
        if (!this.tooltipService.visible) {
            return;
        }
        const target = event.target as HTMLElement;
        if (target.closest('.section-tooltip') || this.answerLink(target)) {
            return;
        }
        this.tooltipService.hide();
    }

    onEscape(): void {
        this.tooltipService.hide();
    }

    private isAwayFromEnd(): boolean {
        const box = this.scroller?.nativeElement;
        return !!box && box.scrollHeight - box.scrollTop - box.clientHeight > AWAY_FROM_END;
    }

    private answerLink(target: EventTarget | null): HTMLElement | null {
        return target instanceof Element ? target.closest<HTMLElement>('.answer a') : null;
    }

    private showTooltip(url: string, x: number, y: number): void {
        // Viewport adjustment for positioning
        const tooltipWidth = 600;
        // A section preview can fill the panel; the "Not verified" explanation is about two lines
        const notVerified = url === NOT_VERIFIED_ANCHOR;
        const tooltipMaxHeight = notVerified ? 70 : 400;
        const edgePadding = 40;
        const rightEdgePadding = 70;

        const viewportWidth = window.innerWidth;
        if (x + tooltipWidth + rightEdgePadding > viewportWidth) {
            x = Math.max(edgePadding, viewportWidth - tooltipWidth - rightEdgePadding);
        }

        const viewportHeight = window.innerHeight;
        if (y + tooltipMaxHeight + edgePadding > viewportHeight) {
            // Above the link instead: clear of the pill for the short panel
            y = Math.max(edgePadding, y - tooltipMaxHeight - (notVerified ? 35 : 20));
        }

        if (notVerified) {
            this.tooltipService.showText(NOT_VERIFIED_EXPLANATION, x, y);
        } else {
            this.tooltipService.showFromUrl(url, x, y);
        }
    }

    /**
     * Bring the question just asked to the top of the page, where its answer unfolds beneath
     * it. The last question and answer are given the height of the page so that it can get there.
     */
    private scrollToLastQuestion(smooth: boolean = false): void {
        const box = this.scroller?.nativeElement;
        const turns = box?.querySelectorAll<HTMLElement>('.turn');
        if (box && turns?.length) {
            box.scrollTo({ top: turns[turns.length - 1].offsetTop, behavior: smooth ? 'smooth' : 'auto' });
        }
    }

    /** Once what has just changed is on the page. */
    private afterRender(action: () => void): void {
        setTimeout(action);
    }

    private stored(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch {
            return null;
        }
    }

    private store(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch {
            // Remembering it is a convenience
        }
    }
}
